/**
 * Spotify OAuth (Authorization Code + PKCE — no client secret in the browser).
 *
 * Identity SSO and playback share one grant: the same tokens sign the user in
 * (backend verifies them against /v1/me) and feed the Web Playback SDK.
 * Tokens live in localStorage with silent refresh; access tokens last 1h.
 */
const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
const SCOPES = 'user-read-email user-read-private streaming user-modify-playback-state user-read-playback-state';
const STORE_KEY = 'spotify_tokens_v1';
const VERIFIER_KEY = 'spotify_pkce_verifier';

export const isSpotifyConfigured = () => Boolean(CLIENT_ID);

export const spotifyRedirectUri = () => `${window.location.origin}/spotify-callback`;

const base64url = (bytes) =>
    btoa(String.fromCharCode(...new Uint8Array(bytes)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

const store = (tokens) => {
    localStorage.setItem(STORE_KEY, JSON.stringify({
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token || loadTokens()?.refresh_token,
        expires_at: Date.now() + (tokens.expires_in - 60) * 1000,
    }));
};

const loadTokens = () => {
    try {
        return JSON.parse(localStorage.getItem(STORE_KEY));
    } catch (e) {
        return null;
    }
};

export const isSpotifyConnected = () => Boolean(loadTokens()?.refresh_token);

export const disconnectSpotify = () => localStorage.removeItem(STORE_KEY);

/** Kick off the redirect flow. Ends with /spotify-callback?code=... */
export async function spotifyLogin() {
    const verifier = base64url(crypto.getRandomValues(new Uint8Array(48)));
    localStorage.setItem(VERIFIER_KEY, verifier);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const params = new URLSearchParams({
        client_id: CLIENT_ID,
        response_type: 'code',
        redirect_uri: spotifyRedirectUri(),
        scope: SCOPES,
        code_challenge_method: 'S256',
        code_challenge: base64url(digest),
    });
    window.location.href = `https://accounts.spotify.com/authorize?${params}`;
}

async function tokenRequest(body) {
    const res = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: new URLSearchParams({client_id: CLIENT_ID, ...body}),
    });
    if (!res.ok) {
        throw new Error(`spotify token endpoint: ${res.status}`);
    }
    return res.json();
}

/** Callback half of the flow: code -> tokens (stored). Returns access_token. */
export async function exchangeSpotifyCode(code) {
    const verifier = localStorage.getItem(VERIFIER_KEY);
    localStorage.removeItem(VERIFIER_KEY);
    const tokens = await tokenRequest({
        grant_type: 'authorization_code',
        code,
        redirect_uri: spotifyRedirectUri(),
        code_verifier: verifier,
    });
    store(tokens);
    return tokens.access_token;
}

/** Valid access token or null (never throws). Refreshes silently when stale. */
export async function getSpotifyToken() {
    const tokens = loadTokens();
    if (!tokens) {
        return null;
    }
    if (tokens.expires_at > Date.now()) {
        return tokens.access_token;
    }
    if (!tokens.refresh_token) {
        return null;
    }
    try {
        const fresh = await tokenRequest({
            grant_type: 'refresh_token',
            refresh_token: tokens.refresh_token,
        });
        store(fresh);
        return fresh.access_token;
    } catch (e) {
        disconnectSpotify(); // refresh token revoked/expired — force a re-login
        return null;
    }
}
